<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { type Patient } from '@/api/patients';
import { patientDocumentsApi } from '@/api/patient-documents';
import PatientDocumentForm from '@/components/patients/patient-document-form.vue';
import ClinicalRecordHistory from '@/components/clinical-records/clinical-record-history.vue';
import ClinicalRecordVoid from '@/components/clinical-records/clinical-record-void.vue';
import ClinicalVoidMark from '@/components/clinical-records/clinical-void-mark.vue';
import ShowVoidedSwitch from '@/components/clinical-records/show-voided-switch.vue';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { usePermissions } from '@/composables/usePermissions';
import { useShowVoided } from '@/composables/useShowVoided';
import ClinicalAuthorLine from '@/components/clinical/clinical-author-line.vue';

interface Props {
  patient: Patient;
}

const props = defineProps<Props>();

const { tableProps } = useResponsiveTable();
const { hasPermission } = usePermissions();

const headers = [
  { title: 'Nombre', value: 'name' },
  { title: 'Acciones', value: 'actions', sortable: false },
];

const { data: patientDocuments } = useQuery({
  queryKey: ['patientDocuments', props.patient.id],
  queryFn: () => patientDocumentsApi.index(props.patient.id),
});

// A document is anulado, never deleted: it stays listed on demand, crossed out.
const { showVoided, voidedCount, visibleItems } = useShowVoided(patientDocuments);
</script>
<template>
  <v-data-table
    v-bind="tableProps"
    :headers="headers"
    :header-props="{ class: 'font-weight-bold' }"
    :items="visibleItems"
    class="elevation-1"
    no-data-text="No hay documentos"
  >
    <template #top>
      <v-toolbar
        color="white"
        flat
      >
        <show-voided-switch
          v-model="showVoided"
          :count="voidedCount"
          class="ms-4"
        />
        <v-spacer />
        <v-dialog
          v-if="hasPermission('patients', 'update')"
          max-width="800"
          scrollable
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-bind="activatorProps"
              icon="mdi-plus"
              color="primary"
              size="small"
              elevation="2"
            />
          </template>
          <template #default="{ isActive }">
            <patient-document-form
              :patient-id="props.patient.id"
              mode="new"
              @close="isActive.value = false"
            />
          </template>
        </v-dialog>
      </v-toolbar>
    </template>
    <template #item.name="{ item }">
      <div>
        <span :class="{ 'text-decoration-line-through text-medium-emphasis': item.voided }">
          {{ item.name }}
        </span>
        <clinical-record-history
          item-type="PatientDocument"
          :item-id="item.id"
          :edited="item.edited"
          :label="`Documento ${item.name}`"
        />
      </div>
      <clinical-author-line
        :author="item.author"
        :at="item.humanCreatedAt"
      />
      <clinical-void-mark
        :voided="item.voided"
        :details="item.voidDetails"
      />
    </template>
    <template #item.actions="{ item }">
      <v-btn
        ref="noopener noreferrer"
        target="_blank"
        :href="item.documentUrl"
        icon="mdi-eye"
        size="small"
        class="me-2"
        :aria-label="`Ver documento ${item.name}`"
      />
      <!-- Renaming: its Autor, within its 24-hour ventana de edición. -->
      <v-dialog
        v-if="hasPermission('patients', 'update') && item.canEdit"
        max-width="800"
        scrollable
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-bind="activatorProps"
            icon="mdi-pencil"
            size="small"
            class="me-2"
            :aria-label="`Editar documento ${item.name}`"
          />
        </template>
        <template #default="{ isActive }">
          <patient-document-form
            :patient-document="item"
            :patient-id="props.patient.id"
            mode="edit"
            @close="isActive.value = false"
          />
        </template>
      </v-dialog>
      <clinical-record-void
        v-if="hasPermission('patients', 'update')"
        item-type="PatientDocument"
        :item-id="item.id"
        :can-void="item.canVoid"
        :label="`Documento ${item.name}`"
      />
    </template>
  </v-data-table>
</template>
